import type { ReactNode } from "react";
import { ModuleSummaryStrip, type ModuleSummaryStat } from "@/components/module_summary/ModuleSummaryStrip";
import { WizardStepIndicator, type WizardStep } from "@/components/module_summary/WizardStepIndicator";
import {
  moduleWorkspaceHeaderTitleRowClass,
  moduleWorkspaceHeaderWizardClass,
  moduleWorkspaceSummaryStripClass,
} from "@/components/module_summary/moduleWorkspaceLayout";
import { cn } from "@/lib/utils";

export type ModuleWorkspaceSummaryConfig = {
  stats?: ModuleSummaryStat[];
  loading?: boolean;
  action?: { label: string; onClick: () => void };
  lead?: ReactNode;
  insight?: ReactNode;
  testId?: string;
};

export type ModuleWorkspaceWizardConfig = {
  steps: WizardStep[];
  currentStep: number;
  onStepClick?: (step: number) => void;
  isStepClickable?: (step: number) => boolean;
  isStepComplete?: (step: number) => boolean;
  testId?: string;
};

export type ModuleWorkspaceHeaderProps = {
  title: string;
  subtitle?: ReactNode;
  leading?: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
  summary?: ModuleWorkspaceSummaryConfig;
  wizard?: ModuleWorkspaceWizardConfig;
  /** Tighter title row for focus layouts (e.g. document preview + chat). */
  density?: "default" | "compact";
  /** When true, omits the title/subtitle row (e.g. nested in a tabbed workspace shell). */
  hideTitle?: boolean;
  className?: string;
  "data-testid"?: string;
};

/**
 * Standard in-module header: title row, optional KPI strip, optional wizard steps.
 */
export function ModuleWorkspaceHeader({
  title,
  subtitle,
  leading,
  meta,
  actions,
  summary,
  wizard,
  density = "default",
  hideTitle = false,
  className,
  "data-testid": testId,
}: ModuleWorkspaceHeaderProps) {
  const compact = density === "compact";

  return (
    <header className={cn("shrink-0 border-b bg-background", className)} data-testid={testId}>
      <div className={cn(moduleWorkspaceHeaderTitleRowClass, compact && "items-center py-2 sm:py-2.5")}>
        <div className="flex min-w-0 items-start gap-3">
          {leading}
          <div className="min-w-0 space-y-1">
            {!hideTitle ? (
              <>
                <h1 className={cn("font-bold", compact ? "text-lg sm:text-xl" : "text-2xl")}>{title}</h1>
                {subtitle && (
                  <p className={cn("text-muted-foreground", compact && "text-sm")}>{subtitle}</p>
                )}
              </>
            ) : null}
            {meta}
          </div>
        </div>
        {actions && <div className="flex max-w-full shrink-0 items-center gap-2">{actions}</div>}
      </div>

      {summary && (
        <ModuleSummaryStrip
          className={moduleWorkspaceSummaryStripClass}
          loading={summary.loading}
          stats={summary.stats ?? []}
          action={summary.action}
          lead={summary.lead}
          data-testid={summary.testId}
        >
          {summary.insight}
        </ModuleSummaryStrip>
      )}

      {wizard && (
        <div className={moduleWorkspaceHeaderWizardClass}>
          <WizardStepIndicator
            steps={wizard.steps}
            currentStep={wizard.currentStep}
            onStepClick={wizard.onStepClick}
            isStepClickable={wizard.isStepClickable}
            isStepComplete={wizard.isStepComplete}
            data-testid={wizard.testId}
          />
        </div>
      )}
    </header>
  );
}
